import { useI18n } from '@/i18n/context';
/**
 * العلامة: الشعار الرسمي المنفرد (`/brand/rafrafa-mark.webp`) — بلا كلامٍ تحته
 * ولا رسمٍ بديل (قرار 13 سبتمبر 2026). لا يُدوَّر ولا يُمدَّد ولا يُلوَّن.
 */
export function LogoMark({ size = 32, className }: { size?: number; className?: string }) {
  // نسبة الشعار الرسمي 900×1066 — الارتفاع يُشتقّ من العرض فلا يتشوّه
  const height = Math.round((size * 1066) / 900);
  return (
    <img
      src="/brand/rafrafa-mark.webp" alt="" width={size} height={height} decoding="async"
      className={className} style={{ inlineSize: size, blockSize: height, objectFit: 'contain' }}
    />
  );
}

/** الشعار مع اسم المنصة في رأس الشريط الجانبي — الاسم بلغة الواجهة (رفرفة / Rafrafa)، والشعار بمقاسٍ ثابت */
export function BrandLockup() {
  const { t } = useI18n();
  return (
    <div className="row gap-3" style={{ alignItems: 'center' }}>
      <LogoMark size={30} className="sidebar__mark" />
      <span className="sidebar__name">{t('brand.name')}</span>
    </div>
  );
}

/**
 * رسومُ الحالات: لكل حالةٍ فارغة رسمٌ يمثّلها هي — لا رمزٌ واحد لكل شيء.
 * خطوطٌ بلون النص الحالي (`currentColor`) فتعمل في الليل والنهار بلا تعديل.
 */
export type StateArtKind =
  | 'empty'      // لا سجلات بعد — مجلّد مفتوح فارغ
  | 'search'     // لا نتائج للبحث
  | 'inbox'      // صندوق وارد فارغ
  | 'done'       // لا شيء ينتظرك
  | 'people'     // لا أشخاص/أعضاء
  | 'child'      // طفل/طالب
  | 'file'       // ملف/وثيقة
  | 'calendar'   // لا مواعيد/جلسات
  | 'locked'     // بلا صلاحية/مقفل
  | 'news'       // لا أخبار
  | 'upload';    // لا مرفقات

const PATHS: Record<StateArtKind, string> = {
  empty: 'M14 40h92v58a6 6 0 0 1-6 6H20a6 6 0 0 1-6-6V40Zm0 0V30a6 6 0 0 1 6-6h26l10 10h44a6 6 0 0 1 6 6M40 70h40',
  search: 'M52 84a30 30 0 1 0 0-60 30 30 0 0 0 0 60Zm22-8 26 26M42 54h20',
  inbox: 'M18 66h24l6 12h24l6-12h24M18 66v26a6 6 0 0 0 6 6h72a6 6 0 0 0 6-6V66M18 66l14-34h56l14 34M60 44v14m-8-8 8 8 8-8',
  done: 'M60 104a44 44 0 1 0 0-88 44 44 0 0 0 0 88Zm-18-44 12 12 26-26',
  people: 'M46 58a14 14 0 1 0 0-28 14 14 0 0 0 0 28Zm34-4a11 11 0 1 0 0-22 11 11 0 0 0 0 22ZM18 96c0-16 12-26 28-26s28 10 28 26M74 88c2-10 10-16 20-16 8 0 14 4 18 10',
  child: 'M60 56a16 16 0 1 0 0-32 16 16 0 0 0 0 32Zm-26 44c0-16 11-26 26-26s26 10 26 26M46 30c4-8 24-8 28 0',
  file: 'M32 16h36l20 20v66a6 6 0 0 1-6 6H32a6 6 0 0 1-6-6V22a6 6 0 0 1 6-6Zm36 0v20h20M44 62h32M44 78h32',
  calendar: 'M22 34h76v58a6 6 0 0 1-6 6H28a6 6 0 0 1-6-6V34Zm0 18h76M40 22v20m40-20v20M44 72h8m12 0h8',
  locked: 'M34 56h52a6 6 0 0 1 6 6v34a6 6 0 0 1-6 6H34a6 6 0 0 1-6-6V62a6 6 0 0 1 6-6Zm8 0V42a18 18 0 0 1 36 0v14M60 74v10',
  news: 'M24 26h64a6 6 0 0 1 6 6v56a8 8 0 0 0 8 8H32a8 8 0 0 1-8-8V26Zm70 70a8 8 0 0 0 8-8V46h-8M36 42h20v18H36zm30 0h18M66 52h18M36 72h48M36 84h32',
  upload: 'M60 88V40m-18 18 18-18 18 18M24 76v14a6 6 0 0 0 6 6h60a6 6 0 0 0 6-6V76',
};

export function StateArt({ kind = 'empty', className }: { kind?: StateArtKind; className?: string }) {
  return (
    <svg viewBox="0 0 120 120" className={className} fill="none" aria-hidden="true" focusable="false">
      <path d={PATHS[kind]} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
